import axios, { AxiosInstance } from 'axios'
import { getCsrfToken } from './utils'
import type {
  PipelineRun,
  PipelineRunsResponse,
  PageItem,
  PageItemsResponse,
  CreatePipelineRunPayload,
} from '@/types/ingestion'

function getBackendURL(): string {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL.replace(/\/api$/, '')
  }

  const hostname = window.location.hostname

  if (hostname === 'localhost' || hostname.includes('.localhost')) {
    return ''
  }

  if (hostname === 'app.devfctrz.com' || hostname.endsWith('.devfctrz.com')) {
    return `${window.location.protocol}//api.devfctrz.com`
  }

  if (hostname === 'app.chatterpillar.io' || hostname.endsWith('.chatterpillar.io')) {
    return `${window.location.protocol}//api.chatterpillar.io`
  }

  return ''
}

const BACKEND_URL = getBackendURL()

const ingestionApi: AxiosInstance = axios.create({
  baseURL: BACKEND_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,
})

function getTenantSubdomain(): string | null {
  try {
    const stored = localStorage.getItem('tenant')
    if (stored) {
      const tenant = JSON.parse(stored)
      if (tenant.subdomain) {
        return tenant.subdomain
      }
    }
  } catch (e) {
    console.error('[Ingestion API] Error reading tenant from localStorage:', e)
  }

  const hostname = window.location.hostname

  if (hostname === 'app.devfctrz.com' || hostname === 'app.chatterpillar.io' || hostname === 'app.localhost') {
    return null
  }

  if (hostname.includes('.devfctrz.com')) {
    const parts = hostname.split('.devfctrz.com')[0]
    return parts.includes('.') ? null : parts
  }
  if (hostname.includes('.chatterpillar.io')) {
    const parts = hostname.split('.chatterpillar.io')[0]
    return parts.includes('.') ? null : parts
  }
  if (hostname.includes('.localhost')) {
    const parts = hostname.split('.localhost')[0]
    return parts.includes('.') ? null : parts
  }

  return null
}

ingestionApi.interceptors.request.use((config) => {
  const csrfToken = getCsrfToken()
  if (csrfToken) {
    config.headers['X-CSRFToken'] = csrfToken
  }

  const tenantSubdomain = getTenantSubdomain()
  if (tenantSubdomain) {
    config.headers['X-Tenant-Subdomain'] = tenantSubdomain
  }

  return config
})

ingestionApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export async function getRuns(): Promise<PipelineRunsResponse> {
  const response = await ingestionApi.get('/ingestion/runs/')
  return response.data
}

export async function createRun(payload: CreatePipelineRunPayload): Promise<PipelineRun> {
  const response = await ingestionApi.post('/ingestion/runs/', payload)
  return response.data
}

export async function getRun(runId: number | string): Promise<PipelineRun> {
  const response = await ingestionApi.get(`/ingestion/runs/${runId}/`)
  return response.data
}

export async function cancelRun(runId: number | string): Promise<PipelineRun> {
  const response = await ingestionApi.post(`/ingestion/runs/${runId}/cancel/`)
  return response.data
}

export async function deleteRun(runId: number | string): Promise<void> {
  await ingestionApi.delete(`/ingestion/runs/${runId}/`)
}

export async function getPages(runId: number | string): Promise<PageItemsResponse> {
  const response = await ingestionApi.get(`/ingestion/runs/${runId}/pages/`)
  return response.data
}

export async function getPage(runId: number | string, pageId: number): Promise<PageItem> {
  const response = await ingestionApi.get(`/ingestion/runs/${runId}/pages/${pageId}/`)
  return response.data
}

export default ingestionApi
